import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import '../styles/Books.css';
import SecureBookReader from './SecureBookReader';

/* ════════════════════════════════════════════════════════════
   FALLBACK DATA — used when Supabase is unreachable
   ════════════════════════════════════════════════════════════ */
const SEED_BOOKS = [
  {
    id: 'bk-hanin',
    title: 'حنين في همسات',
    year: 1994,
    type: 'خواطر شعرية',
    desc: 'أولى إصداراته الأدبية — خواطر شعرية نابعة من حنين الغربة وذكريات الجبل.',
    accent: '#8B7355',
    coverUrl: null,
    poemCount: null,
  },
  {
    id: 'bk-radhad',
    title: 'رذاذ على العشب',
    year: 2003,
    type: 'شذرات شعرية',
    desc: 'نثرات أدبية رقيقة تمزج بين الشعر والنثر في أسلوب متفرّد.',
    accent: '#6B7B8D',
    coverUrl: null,
    poemCount: null,
  },
  {
    id: 'bk-liannak',
    title: 'لأنكِ أنتِ… أنا',
    year: 2005,
    type: 'ديوان شعري',
    desc: 'ديوان غزلي يستكشف ذوبان الذات في الآخر — حيث يصبح الحبيب امتداداً للروح.',
    accent: '#9B4D6C',
    coverUrl: null,
    poemCount: null,
  },
  {
    id: 'bk-qatarat',
    title: 'قطراتُ شوق',
    year: 2008,
    type: 'ديوان شعري',
    desc: 'قطرات من الشوق والحنين تتساقط على أوراق القصيدة في ليالي الغربة.',
    accent: '#4A7C8C',
    coverUrl: null,
    poemCount: null,
  },
  {
    id: 'bk-bouh',
    title: 'بوح السنابل',
    year: 2011,
    type: 'ديوان شعري',
    desc: 'يجمع بين الغزل العذري والوطنية الصادقة — أحد أبرز إصداراته وأكثرها تأثيراً.',
    accent: '#4A7C59',
    coverUrl: null,
    poemCount: 21,
  },
  {
    id: 'bk-uktubini',
    title: 'اكتبيني بعد…',
    year: 2014,
    type: 'ديوان شعري',
    desc: 'نداء الكلمة المتأخرة — حين يطلب الشعر من شاعره أن يُعيد كتابته.',
    accent: '#7A6B5B',
    coverUrl: null,
    poemCount: null,
  },
  {
    id: 'bk-majamir',
    title: 'مجامر ورد',
    year: 2018,
    type: 'ديوان شعري',
    desc: 'مجامر تتصاعد منها أبخرة الورد والعشق — ديوان يشتعل بنار الحب الهادئة.',
    accent: '#A0522D',
    coverUrl: null,
    poemCount: null,
  },
  {
    id: 'bk-iqaat',
    title: 'إيقاعات ممنوعة',
    year: 2021,
    type: 'ديوان شعري',
    desc: 'ديوان يكسر الحواجز بإيقاعات جريئة — يعبّر عن مشاعر الوجد والتحرر.',
    accent: '#7A3B2E',
    coverUrl: null,
    poemCount: 16,
  },
  {
    id: 'bk-lam',
    title: 'لم يبقَ لي غير الكلمات',
    year: 2024,
    type: 'ديوان شعري',
    desc: 'رحلة بين الغزل والوطنية، وما تبقّى له سوى الكلمة — أحدث الدواوين الكاملة.',
    accent: '#C4A44A',
    coverUrl: null,
    poemCount: 24,
  },
  {
    id: 'bk-lahz',
    title: 'لحظٌ في عيون المساء',
    year: 2026,
    type: 'ديوان شعري',
    desc: 'أحدث إصدار 2026 — يرقب لحظات العشق في عيون الغسق والمساء.',
    accent: '#2D6A4F',
    coverUrl: null,
    poemCount: 15,
    isLatest: true,
  },
];

/* ════════════════════════════════════════════════════════════ */

export default function Books() {
  const [books, setBooks] = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);
  const [revealed, setRevealed] = useState(new Set());
  const [poemCount, setPoemCount] = useState({});
  const [readingPdf, setReadingPdf] = useState(null); // { book, part }
  const cardRefs = useRef([]);
  const gridRef = useRef(null);

  /* ── Load books from Supabase (fallback to SEED_BOOKS) ── */
  useEffect(() => {
    const fetchBooks = async () => {
      try {
        const { data, error } = await supabase
          .from('books')
          .select('*')
          .order('sort_order', { ascending: true });

        if (error) throw error;

        if (data && data.length > 0) {
          setBooks(data.map(b => ({
            id: b.id,
            title: b.title,
            year: b.year,
            type: b.type,
            desc: b.description,
            accent: b.accent || '#C4A44A',
            coverUrl: b.cover_url,
            pdfUrl: b.pdf_url,
            pdfParts: b.pdf_parts || [],
            buyLink: b.buy_link,
            isLatest: b.is_latest,
            allowFullRead: b.allow_full_read === true, // false = preview only
            previewPages: b.preview_pages || 10,
          })));
        } else {
          setBooks(SEED_BOOKS);
        }
      } catch (_) {
        setBooks(SEED_BOOKS);
      }
    };

    fetchBooks();

    // Listen for realtime updates
    const channel = supabase
      .channel('books_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'books' }, () => {
        fetchBooks();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  /* ── Fetch poem counts per book ──────────────────────── */
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const { data, error } = await supabase
          .from('book_poems')
          .select('book_id');
        if (error || !data) return;
        const counts = {};
        data.forEach(p => {
          counts[p.book_id] = (counts[p.book_id] || 0) + 1;
        });
        setPoemCount(counts);
      } catch (_) {}
    };
    fetchCounts();
  }, []);

  /* ── Intersection observer for scroll reveal ──────── */
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) setRevealed(p => new Set([...p, e.target.dataset.bid]));
      }),
      { threshold: 0.08 }
    );
    cardRefs.current.forEach(r => r && obs.observe(r));
    return () => obs.disconnect();
  }, [books]);

  /* ── Close overlay on Escape ──────────────────────── */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (readingPdf) setReadingPdf(null);
        else setSelectedBook(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [readingPdf]);

  /* ── Lock body scroll when overlay is open ─────────── */
  useEffect(() => {
    if (selectedBook) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [selectedBook]);

  const openBook = (book) => setSelectedBook(book);
  const closeBook = () => setSelectedBook(null);

  /* ── Open the protected reader (canvas, no browser PDF viewer) ── */
  const openPdf = (book, part = 0) => {
    setReadingPdf({ book, part });
  };

  return (
    <section id="books" className="books-section">
      {/* Ambient floating letters */}
      <div className="books-ambient" aria-hidden="true">
        <b>ش</b><b>ع</b><b>ر</b>
      </div>

      {/* Particle dots */}
      <div className="books-particles" aria-hidden="true">
        {[...Array(20)].map((_, i) => (
          <span
            key={i}
            className="particle"
            style={{
              '--x': `${10 + Math.random() * 80}%`,
              '--y': `${5 + Math.random() * 90}%`,
              '--dur': `${3 + Math.random() * 5}s`,
              '--del': `${Math.random() * 4}s`,
              '--size': `${2 + Math.random() * 3}px`,
            }}
          />
        ))}
      </div>

      <div className="books-container">

        {/* ── Header ─────────────────────────────────────── */}
        <header className="books-header">
          <p className="bk-eyebrow">الأعمال الأدبية الكاملة</p>
          <h2 className="bk-h2">الإصدارات</h2>
          <div className="bk-rule"><span /><em>◆</em><span /></div>
          <p className="bk-sub">
            {books.length} إصدار أدبي · من عام 1994 حتى 2026
          </p>
        </header>

        {/* ── Books Collection Showcase ─────────────────── */}
        {/* Full-width banner showing all 10 diwans together */}
        <div className="bk-collection-banner">
          <img
            src="/books-collection.jpg"
            alt="مجموعة الدواوين الشعرية الكاملة للشاعر وجدي عبد الصمد"
            className="bk-collection-img"
            loading="lazy"
          />
          <p className="bk-collection-caption">عشرة دواوين شعرية · رحلة ثلاثة عقود مع الكلمة</p>
        </div>

        {/* ── Timeline ribbon ──────────────────────────────── */}
        <div className="bk-timeline">
          <div className="bk-timeline-line" />
          {books.map((book, idx) => (
            <button
              key={book.id}
              className={`bk-timeline-dot${selectedBook?.id === book.id ? ' active' : ''}`}
              style={{
                '--pos': `${(idx / (books.length - 1)) * 100}%`,
                '--acc': book.accent,
              }}
              onClick={() => openBook(book)}
              title={`${book.title} — ${book.year}`}
              aria-label={book.title}
            >
              <span className="bk-timeline-year" dir="ltr">{book.year}</span>
            </button>
          ))}
        </div>

        {/* ── Book Grid ────────────────────────────────────── */}
        <div className="books-grid" ref={gridRef}>
          {books.map((book, idx) => (
            <div
              key={book.id}
              ref={el => { cardRefs.current[idx] = el; }}
              data-bid={book.id}
              className={`bk-card ${revealed.has(book.id) ? 'bk-card--in' : ''} ${book.isLatest ? 'bk-card--latest' : ''}`}
              style={{ '--bk-acc': book.accent, '--delay': `${idx * 70}ms` }}
              onClick={() => openBook(book)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && openBook(book)}
              aria-label={`اكتشف ديوان ${book.title}`}
            >
              {/* 3D tilt wrapper */}
              <div className="bk-card-inner">
                {/* Cover */}
                <div className="bk-card-cover">
                  {book.coverUrl ? (
                    <img src={book.coverUrl} alt={`غلاف ${book.title}`} />
                  ) : (
                    <div className="bk-card-cover-ph">
                      <div className="bk-card-spine" style={{ background: book.accent }} />
                      <div className="bk-card-cover-body">
                        <div className="bk-card-cover-ornament">✦</div>
                        <h4 className="bk-card-cover-title">{book.title}</h4>
                        <div className="bk-card-cover-divider" style={{ background: book.accent }} />
                        <span className="bk-card-cover-author">الشاعر وجدي عبد الصمد</span>
                        <span className="bk-card-cover-year" dir="ltr">{book.year}</span>
                      </div>
                    </div>
                  )}
                  {book.isLatest && <span className="bk-card-badge">جديد 2026</span>}
                  <div className="bk-card-cover-glow" style={{ background: book.accent }} />
                </div>

                {/* Info */}
                <div className="bk-card-info">
                  <div className="bk-card-top">
                    <span className="bk-card-year" dir="ltr">{book.year}</span>
                    <span className="bk-card-type">{book.type}</span>
                  </div>
                  <h3 className="bk-card-title">{book.title}</h3>
                  <div className="bk-card-rule" style={{ background: book.accent }} />
                  <p className="bk-card-desc">{book.desc}</p>
                  <div className="bk-card-bottom">
                    {poemCount[book.id] > 0 && (
                      <span className="bk-card-poems">{poemCount[book.id]} قصيدة</span>
                    )}
                    <span className="bk-card-cta">اكتشف ←</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
          FULL-SCREEN BOOK OVERLAY
         ══════════════════════════════════════════════════════ */}
      {selectedBook && (
        <div
          className="bk-overlay"
          onClick={closeBook}
          role="dialog"
          aria-modal="true"
          aria-label={`ديوان ${selectedBook.title}`}
        >
          <div className="bk-overlay-content" onClick={(e) => e.stopPropagation()}>
            {/* Close button */}
            <button className="bk-overlay-close" onClick={closeBook} aria-label="إغلاق">
              ✕
            </button>

            {/* Background accent glow */}
            <div className="bk-overlay-glow" style={{ background: selectedBook.accent }} />

            {/* Two-column layout: cover + info */}
            <div className="bk-overlay-layout">

              {/* Cover side */}
              <div className="bk-overlay-cover-wrap">
                <div className="bk-overlay-cover" style={{ '--acc': selectedBook.accent }}>
                  {selectedBook.coverUrl ? (
                    <img src={selectedBook.coverUrl} alt={`غلاف ${selectedBook.title}`} />
                  ) : (
                    <div className="bk-overlay-cover-ph">
                      <div className="bk-overlay-spine" style={{ background: selectedBook.accent }} />
                      <div className="bk-overlay-cover-inner">
                        <div className="bk-oc-ornament">❖</div>
                        <h3 className="bk-oc-title">{selectedBook.title}</h3>
                        <div className="bk-oc-line" style={{ background: selectedBook.accent }} />
                        <p className="bk-oc-author">الشاعر وجدي عبد الصمد</p>
                        <span className="bk-oc-year" dir="ltr">{selectedBook.year}</span>
                      </div>
                    </div>
                  )}
                </div>
                {/* Shadow under the book */}
                <div className="bk-overlay-shadow" />
              </div>

              {/* Info side */}
              <div className="bk-overlay-info">
                <div className="bk-overlay-meta">
                  <span className="bk-overlay-type" style={{ borderColor: selectedBook.accent, color: selectedBook.accent }}>
                    {selectedBook.type}
                  </span>
                  <span className="bk-overlay-year" dir="ltr">{selectedBook.year}</span>
                </div>

                <h2 className="bk-overlay-title">{selectedBook.title}</h2>

                <div className="bk-overlay-bar" style={{ background: selectedBook.accent }} />

                <p className="bk-overlay-author-line">
                  <span className="bk-overlay-author-icon">✒️</span>
                  الشاعر وجدي عبد الصمد
                </p>

                <p className="bk-overlay-desc">{selectedBook.desc}</p>

                {poemCount[selectedBook.id] > 0 && (
                  <div className="bk-overlay-stat">
                    <span className="bk-overlay-stat-num">{poemCount[selectedBook.id]}</span>
                    <span className="bk-overlay-stat-label">قصيدة في هذا الديوان</span>
                  </div>
                )}

                <div className="bk-overlay-actions">
                  {/* Reading buttons — full mode: one per part · preview mode: one preview button */}
                  {selectedBook.allowFullRead && selectedBook.pdfParts && selectedBook.pdfParts.length > 0 && selectedBook.pdfParts.map((part, idx) => (
                    <button
                      key={idx}
                      className="bk-overlay-btn-primary"
                      style={{ background: selectedBook.accent }}
                      onClick={() => openPdf(selectedBook, idx)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zm-3 9h4v2h-4v-2zm0 3h4v2h-4v-2zm-2-3h1v2H8v-2zm0 3h1v2H8v-2z"/></svg>
                      {part.name || 'قراءة الديوان'}
                    </button>
                  ))}
                  {((selectedBook.pdfParts && selectedBook.pdfParts.length > 0) || selectedBook.pdfUrl) &&
                    !(selectedBook.allowFullRead && selectedBook.pdfParts && selectedBook.pdfParts.length > 0) && (
                    <button
                      className="bk-overlay-btn-primary"
                      style={{ background: selectedBook.accent }}
                      onClick={() => openPdf(selectedBook, 0)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zm-3 9h4v2h-4v-2zm0 3h4v2h-4v-2zm-2-3h1v2H8v-2zm0 3h1v2H8v-2z"/></svg>
                      {selectedBook.allowFullRead ? 'قراءة الديوان' : 'تصفّح مقتطفات من الديوان'}
                    </button>
                  )}
                  {/* Buy button */}
                  {selectedBook.buyLink && (
                    <a
                      href={selectedBook.buyLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bk-overlay-btn-buy"
                    >
                      🛒 اطلب ديوانك
                    </a>
                  )}
                  {poemCount[selectedBook.id] > 0 && (
                    <button className="bk-overlay-btn-secondary" style={{ borderColor: selectedBook.accent, color: selectedBook.accent }}>
                      قصائد الديوان
                    </button>
                  )}
                  <button className="bk-overlay-btn-secondary" onClick={closeBook}>
                    رجوع
                  </button>
                </div>

                {!selectedBook.pdfUrl && (!selectedBook.pdfParts || selectedBook.pdfParts.length === 0) && !poemCount[selectedBook.id] && (
                  <p className="bk-overlay-coming">
                    محتوى هذا الديوان سيُضاف قريباً
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          PROTECTED READER — same on desktop and mobile.
          Pages are drawn on canvas; the browser PDF viewer never loads.
         ══════════════════════════════════════════════════════ */}
      {readingPdf && (
        <SecureBookReader
          key={`${readingPdf.book.id}-${readingPdf.part}`}
          book={readingPdf.book}
          initialPart={readingPdf.part}
          onClose={() => setReadingPdf(null)}
        />
      )}
    </section>
  );
}